import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, Field } from "../components/Shared";
import { formatDateID } from "../components/DateField";
import { RecognitionBoards } from "../components/RecognitionBoards";
import { Trophy, Pin, PinOff, Flame, Sparkles, TrendingDown } from "lucide-react";

const TABS = ["top", "weekly"];

const MEDAL = ["bg-amber-400/15 text-amber-300 border-amber-400/30", "bg-slate-300/10 text-slate-200 border-slate-300/25", "bg-orange-400/10 text-orange-300 border-orange-400/25"];

export default function RecognitionWall() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isMember = user?.role === "MEMBER";
  const [data, setData] = useState(null);
  const [batchId, setBatchId] = useState("");
  const [pinNote, setPinNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("top");

  const changeMode = async (mode) => {
    try {
      await api.put("/admin/recognition-settings", { mode });
      toast.success(t("recog.modeSaved"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/recognition/wall${batchId ? `?batch_id=${batchId}` : ""}`);
      setData(res.data);
      if (!batchId && res.data.batch) setBatchId(res.data.batch.batch_id);
    } catch (e) { toast.error(extractError(e).message); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const pin = async (userId) => {
    setBusy(true);
    try {
      await api.post("/recognition/pin", { batch_id: batchId, user_id: userId, note: pinNote || null });
      toast.success(t("recog.pinned_ok"));
      setPinNote("");
      load();
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(false); }
  };

  const unpin = async () => {
    setBusy(true);
    try {
      await api.delete(`/recognition/pin/${batchId}`);
      toast.success(t("recog.unpinned_ok"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="recognition-page" className="space-y-6">
      <PageHeader eyebrow={t("recog.eyebrow")} title={t("recog.title")} subtitle={t("recog.subtitle")}>
        {!isMember && (data?.batches?.length || 0) > 0 && (
          <select data-testid="recog-batch-select" value={batchId} onChange={(e) => setBatchId(e.target.value)}
            className="h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
            {data.batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{b.code} — {b.name}</option>)}
          </select>
        )}
      </PageHeader>

      <div className="flex gap-2 border-b border-white/5">
        {TABS.map((k) => (
          <button key={k} data-testid={`recog-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-[#FF3B30] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
            {t(`recog.tab_${k}`)}
          </button>
        ))}
      </div>

      {tab === "top" && (
        <RecognitionBoards boards={data?.boards} mode={data?.display_mode} topLimit={data?.top_limit}
          canConfigure={!!data?.can_configure} onModeChange={changeMode} />
      )}

      {tab === "weekly" && (<>
      {data && (
        <div data-testid="recog-week" className="text-xs text-slate-400">
          {t("recog.week")}: {formatDateID(data.week_start, lang)} — {formatDateID(data.week_end, lang)}
        </div>
      )}

      {data?.pinned && (
        <Card data-testid="recog-pinned" className="bg-gradient-to-r from-[#FF3B30]/15 to-transparent border-[#FF3B30]/30">
          <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="h-12 w-12 shrink-0 rounded-2xl bg-[#FF3B30]/20 flex items-center justify-center"><Pin className="h-5 w-5 text-[#FF8A80]" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#FF8A80] font-semibold">{t("recog.pinned")}</div>
              <div className="font-[Outfit] text-xl font-extrabold text-white">{data.pinned.full_name || "—"}</div>
              {data.pinned.note && <p className="text-sm text-slate-300 mt-1">{data.pinned.note}</p>}
            </div>
            {data.can_pin && (
              <Button data-testid="recog-unpin-btn" disabled={busy} variant="ghost" onClick={unpin} className="text-slate-300 hover:bg-white/5">
                <PinOff className="h-4 w-4 mr-1" /> {t("recog.unpin")}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {(data?.highlights || []).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {data.highlights.map((r, i) => (
            <Card key={r.user_id} data-testid={`recog-highlight-${r.user_id}`} className={`bg-[#161923] border ${i === 0 ? "border-amber-400/30" : "border-white/5"}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className={`h-9 w-9 rounded-xl border flex items-center justify-center font-[Outfit] font-extrabold ${MEDAL[i]}`}>{r.rank}</span>
                  {i === 0 && <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold">{t("recog.champion")}</span>}
                </div>
                <div>
                  <div className="font-[Outfit] text-lg font-extrabold text-white truncate">{r.full_name || "—"}</div>
                  <div className="text-[11px] text-slate-500">{t("recog.score")}: <span className="text-white font-bold">{r.score}</span></div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-400">
                  <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-[#10B981]" /> {t("recog.points")}: {r.points}</span>
                  <span className="inline-flex items-center gap-1"><Flame className="h-3 w-3 text-[#FF8A80]" /> {t("recog.streak")}: {r.streak}</span>
                  <span>{t("recog.compliance")}: {Math.round(r.compliance * 100)}%</span>
                  <span className="inline-flex items-center gap-1"><TrendingDown className="h-3 w-3 text-emerald-300" /> {r.weight_lost} kg</span>
                </div>
                {data.can_pin && (
                  <Button data-testid={`recog-pin-${r.user_id}`} disabled={busy} onClick={() => pin(r.user_id)}
                    className="w-full bg-white/10 hover:bg-white/15 text-white h-9"><Pin className="h-4 w-4 mr-1" /> {t("recog.pin")}</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data?.can_pin && (
        <Field label={t("recog.pinNote")} testId="recog-pin-note" value={pinNote} onChange={(e) => setPinNote(e.target.value)} />
      )}

      <Card data-testid="recog-ranking" className="bg-[#10121B] border-white/5">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-base md:text-lg font-semibold text-white inline-flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-300" /> {t("recog.ranking")}</h2>
            <span className="text-[11px] text-slate-500">{t("recog.formula")}</span>
          </div>
          {(data?.rows || []).length === 0 ? <p className="text-sm text-slate-500">{t("recog.empty")}</p> : (
            <div className="space-y-2">
              {data.rows.map((r) => (
                <div key={r.user_id} data-testid={`recog-row-${r.user_id}`}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${r.is_me ? "border-[#FF3B30]/40 bg-[#FF3B30]/[0.07]" : "border-white/5"}`}>
                  <span className="h-8 w-8 shrink-0 rounded-lg bg-white/5 flex items-center justify-center font-[Outfit] font-extrabold text-sm text-slate-300">{r.rank}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white truncate">{r.full_name || "—"}</div>
                    <div className="text-[11px] text-slate-500">
                      {t("recog.points")} {r.points} · {t("recog.streak")} {r.streak} · {t("recog.compliance")} {r.compliance_days}/{r.week_days} · {t("recog.weightLost")} {r.weight_lost} kg · {t("recog.fatLost")} {r.fat_lost}%
                    </div>
                  </div>
                  <div className="shrink-0 font-[Outfit] font-extrabold text-white">{r.score}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {data && (data.rows || []).length === 0 && !data.pinned && <EmptyState testId="recog-empty" text={t("recog.empty")} />}
      </>)}
    </div>
  );
}
